import { useMemo } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  CardSelection,
  Flex,
  FormLayout,
  Image,
  Text,
  UpcomingConnectionsIndicator,
  scrollToInvalidField,
} from "@wayflyer/flyui";
import { useRhfAnalytics } from "@wayflyer/flyui-hook-form";
import {
  IconCheckSquares20Line,
  IconCity20Line,
  IconCube20Line,
  IconGlobe20Line,
  IconGraphUp20Line,
  IconShop20Line,
  IconSuitcase20Line,
  IconUser20Line,
} from "@wayflyer/flyui-icons/20/line";
import { IconMoreHoriz24Line } from "@wayflyer/flyui-icons/24/line";
import type { FundingApplicationSchemas } from "@wayflyer/api-types";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useIntl } from "react-intl";
import { usePostHog } from "posthog-js/react";
import { useErrorModal } from "shared-ui";
import { useCustomerAppConfig } from "@wayflyer/undercarriage";
import type { ObjectSchema } from "yup";

import { useFundingApplication, useFundingApplicationMutation } from "../../queries/funding-application/queries";
import { useConnectionsPreview } from "../../queries/connections-preview/queries";

import { makeAboutYourBusinessSchema } from "./about-your-business.schema";
import { MESSAGES } from "./message-definitions";

type AboutYourBusinessFormFields = {
  whoYouSellTo: FundingApplicationSchemas.CustomerSegment[];
  whereYouSell: FundingApplicationSchemas.SalesChannel[];
  whatYouSell: FundingApplicationSchemas.ProductOffering[];
};

const PLATFORM_LOGOS = {
  plaid: "/fly-assets/logo-icons/plaid-v2.svg",
  shopify: "/fly-assets/logo-icons/shopify.svg",
  amazon: "/fly-assets/logo-icons/amazon.svg",
  xero: "/fly-assets/logo-icons/xero.svg",
  quickbooks: "/fly-assets/logo-icons/quickbooks.svg",
};

export interface AboutYourBusinessProps {
  formId: string;
  onContinue: (data: FundingApplicationSchemas.CustomerFundingApplicationScreen) => void;
}
export const AboutYourBusiness = ({ onContinue, formId }: AboutYourBusinessProps) => {
  const intl = useIntl();
  const posthog = usePostHog();
  const { openErrorModal } = useErrorModal();
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const { data, isFetched } = useFundingApplication(customerDomain);
  const fundingApplicationMutation = useFundingApplicationMutation(customerDomain);

  const initialValues: AboutYourBusinessFormFields = {
    whoYouSellTo: data?.reportedCustomerSegment ?? [],
    whereYouSell: data?.reportedSalesChannel ?? [],
    whatYouSell: data?.reportedProductOffering ?? [],
  };

  const schema = makeAboutYourBusinessSchema(intl);

  const form = useForm<AboutYourBusinessFormFields>({
    // The legacy schema intentionally permits undefined array elements; the UI only
    // produces the generated enum values, so narrow the schema at the form boundary.
    resolver: yupResolver(schema as ObjectSchema<AboutYourBusinessFormFields>),
    defaultValues: initialValues,
  });

  type FormValues = AboutYourBusinessFormFields;

  const watchedWhoYouSellTo = useWatch({ control: form.control, name: "whoYouSellTo" });
  const watchedWhereYouSell = useWatch({ control: form.control, name: "whereYouSell" });
  const watchedWhatYouSell = useWatch({ control: form.control, name: "whatYouSell" });

  const connectionsPreviewRequest = useMemo(
    () => ({
      customerSegments: watchedWhoYouSellTo ?? [],
      salesChannels: watchedWhereYouSell ?? [],
      productOfferings: watchedWhatYouSell ?? [],
    }),
    [watchedWhoYouSellTo, watchedWhereYouSell, watchedWhatYouSell]
  );

  const { data: connectionsPreview } = useConnectionsPreview(customerDomain, connectionsPreviewRequest);

  const bankingPlatforms = [
    {
      logo: (
        <span style={{ display: "flex", filter: "brightness(0) invert(1)" }}>
          <Image src={PLATFORM_LOGOS.plaid} alt="Plaid" width={16} height={16} />
        </span>
      ),
      label: "Plaid",
    },
  ];

  const shoppingPlatforms = [
    { logo: <Image src={PLATFORM_LOGOS.shopify} alt="Shopify" />, label: "Shopify" },
    { logo: <Image src={PLATFORM_LOGOS.amazon} alt="Amazon" />, label: "Amazon" },
    { logo: <IconMoreHoriz24Line />, label: "More" },
  ];

  const accountingPlatforms = [
    { logo: <Image src={PLATFORM_LOGOS.xero} alt="Xero" />, label: "Xero" },
    { logo: <Image src={PLATFORM_LOGOS.quickbooks} alt="QuickBooks" />, label: "QuickBooks" },
    { logo: <IconMoreHoriz24Line />, label: "More" },
  ];

  useRhfAnalytics({
    form,
    captureFn: (eventName, properties) => posthog?.capture(eventName, properties),
    formName: "onboarding_about_your_business",
    fieldsToTrack: ["whoYouSellTo", "whereYouSell", "whatYouSell"],
  });

  const onAboutBusinessSubmit = async (formData: FormValues) => {
    if (!isFetched || !data) {
      return;
    }
    await fundingApplicationMutation
      .mutateAsync({
        reportedCustomerSegment: formData.whoYouSellTo,
        reportedSalesChannel: formData.whereYouSell,
        reportedProductOffering: formData.whatYouSell,
        id: data.id,
      })
      .then((fundingApplicationResult: FundingApplicationSchemas.CustomerFundingApplicationScreen) =>
        onContinue(fundingApplicationResult)
      )
      .catch((error) => {
        openErrorModal({ variant: "submit-error", error });
      });
  };

  const onSubmit = async (formData: FormValues) => {
    await onAboutBusinessSubmit(formData);
  };

  return (
    <>
      <form
        id={formId}
        data-testid="about-your-business-form"
        onSubmit={form.handleSubmit(onSubmit, scrollToInvalidField)}
      >
        <FormLayout>
          <Flex direction="column" gap="3">
            <Text fontWeight="semiBold">{intl.formatMessage(MESSAGES.whoYouSellToLabel)}</Text>
            <Controller
              name="whoYouSellTo"
              control={form.control}
              render={({ field, fieldState }) => (
                <CardSelection
                  {...field}
                  variant="multi"
                  defaultValue={field.value}
                  options={[
                    {
                      value: "b2c",
                      label: intl.formatMessage(MESSAGES.b2cLabel),
                      subtext: intl.formatMessage(MESSAGES.b2cHelperText),
                      icon: <IconUser20Line />,
                    },
                    {
                      value: "b2b",
                      label: intl.formatMessage(MESSAGES.b2bLabel),
                      subtext: intl.formatMessage(MESSAGES.b2bHelperText),
                      icon: <IconCity20Line />,
                    },
                  ]}
                  error={fieldState.error?.message}
                />
              )}
            />
          </Flex>
          <Flex direction="column" gap="3">
            <Text fontWeight="semiBold">{intl.formatMessage(MESSAGES.whereYouSellLabel)}</Text>
            <Controller
              name="whereYouSell"
              control={form.control}
              render={({ field, fieldState }) => (
                <CardSelection
                  {...field}
                  variant="multi"
                  defaultValue={field.value}
                  options={[
                    {
                      value: "online",
                      label: intl.formatMessage(MESSAGES.onlineLabel),
                      subtext: intl.formatMessage(MESSAGES.onlineHelperText),
                      icon: <IconGlobe20Line />,
                    },
                    {
                      value: "offline",
                      label: intl.formatMessage(MESSAGES.offlineLabel),
                      subtext: intl.formatMessage(MESSAGES.offlineHelperText),
                      icon: <IconShop20Line />,
                    },
                  ]}
                  error={fieldState.error?.message}
                />
              )}
            />
          </Flex>
          <Flex direction="column" gap="3">
            <Text fontWeight="semiBold">{intl.formatMessage(MESSAGES.whatYouSellLabel)}</Text>
            <Controller
              name="whatYouSell"
              control={form.control}
              render={({ field, fieldState }) => (
                <CardSelection
                  {...field}
                  variant="multi"
                  defaultValue={field.value}
                  options={[
                    {
                      value: "physical",
                      label: intl.formatMessage(MESSAGES.physicalProductsLabel),
                      subtext: intl.formatMessage(MESSAGES.physicalProductsHelperText),
                      icon: <IconCube20Line />,
                    },
                    {
                      value: "digital_products",
                      label: intl.formatMessage(MESSAGES.digitalProductsLabel),
                      subtext: intl.formatMessage(MESSAGES.digitalProductsHelperText),
                      icon: <IconGraphUp20Line />,
                    },
                    {
                      value: "services",
                      label: intl.formatMessage(MESSAGES.servicesLabel),
                      subtext: intl.formatMessage(MESSAGES.servicesHelperText),
                      icon: <IconSuitcase20Line />,
                    },
                    {
                      value: "subscriptions",
                      label: intl.formatMessage(MESSAGES.subscriptionsLabel),
                      subtext: intl.formatMessage(MESSAGES.subscriptionsHelperText),
                      icon: <IconCheckSquares20Line />,
                    },
                  ]}
                  error={fieldState.error?.message}
                />
              )}
            />
          </Flex>
        </FormLayout>
      </form>
      <UpcomingConnectionsIndicator
        title={intl.formatMessage(MESSAGES.upcomingConnectionsTitle)}
        banking={{ platforms: bankingPlatforms }}
        shopping={{
          platforms: shoppingPlatforms,
          enabled: connectionsPreview?.shopping ?? false,
        }}
        accounting={{
          platforms: accountingPlatforms,
          enabled: connectionsPreview?.accounting ?? false,
        }}
      />
    </>
  );
};
